import React, { useEffect } from 'react';
import type { ReactNode } from 'react';
import useStyles from '../../theme/useStyles';

// Detail panel: header, scrollable label/value rows, sticky footer action.
// Use Drawer.Row / Drawer.Section for the body.
export interface DrawerProps {
  open?: boolean;
  title?: ReactNode;
  subtitle?: ReactNode;
  onClose?: () => void;
  footer?: ReactNode;
  width?: string;
  children?: ReactNode;
  isDarkMode?: boolean;
}

export interface DrawerRowProps {
  label?: ReactNode;
  /** Rendered when `children` is absent; '-' when both are empty. */
  value?: ReactNode;
  children?: ReactNode;
  isDarkMode?: boolean;
}

export interface DrawerSectionProps {
  title?: ReactNode;
  isDarkMode?: boolean;
}

const Drawer = ({ open = true, title, subtitle, onClose, footer, width = '500px', children, isDarkMode }: DrawerProps) => {
  const s = useStyles(isDarkMode);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div style={s.drawerOverlay} onClick={onClose}>
      <div style={{ ...s.drawerPanel, maxWidth: width }} onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <div style={s.drawerHeader}>
          <div>
            <h2 style={s.drawerTitle}>{title}</h2>
            {subtitle && <div style={{ color: 'rgba(255,255,255,0.8)', fontSize: s.t.fontSize.base, marginTop: '4px' }}>{subtitle}</div>}
          </div>
          {onClose && <button style={s.drawerCloseBtn} onClick={onClose} aria-label="Close">✕</button>}
        </div>
        <div style={s.drawerBody}>{children}</div>
        {footer && <div style={s.drawerFooter}>{footer}</div>}
      </div>
    </div>
  );
};

const Row = ({ label, value, children, isDarkMode }: DrawerRowProps) => {
  const s = useStyles(isDarkMode);
  const content = children ?? (value === '' || value === null || value === undefined ? '-' : value);

  // A row with no label carries a block of prose — a note or a description.
  // The label/value split right-aligns it and squeezes it into half the width,
  // which read as broken alignment (CHM-128). Those rows run full width,
  // left-aligned, and keep the line breaks that were typed.
  if (!label) {
    return (
      <div style={{ ...s.drawerRow, display: 'block' }}>
        <span style={{
          ...s.drawerValue,
          display: 'block', textAlign: 'left', fontWeight: 'normal',
          whiteSpace: 'pre-wrap', wordBreak: 'break-word', lineHeight: 1.5
        }}>
          {content}
        </span>
      </div>
    );
  }

  return (
    <div style={s.drawerRow}>
      <span style={s.drawerLabel}>{label}</span>
      <span style={{ ...s.drawerValue, wordBreak: 'break-word' }}>{content}</span>
    </div>
  );
};

const Section = ({ title, isDarkMode }: DrawerSectionProps) => {
  const s = useStyles(isDarkMode);
  return <div style={s.sectionTitle}>{title}</div>;
};

Drawer.Row = Row;
Drawer.Section = Section;

export default Drawer;
